<template>
  <div>
    <div v-if="loading"
         class="bcr-load"><i class="fal fa-spinner-third fa-spin"></i></div>
    <p v-else-if="error"
       class="bcr-error">{{ error }}</p>
    <div v-else-if="b">
      <div class="bcr-head">
        <div class="bcr-title">
          <h4 class="bcr-q">{{ title }}</h4>
          <p class="bcr-note">{{ whenText }} · {{ $t("broadcast.screens.home.from_pages", { n: num(b.pages) }) }}</p>
        </div>
        <BcStatusPill :status="b.status"
                      :paused-reason="b.pausedReason" />
      </div>
      <div v-if="autopaused && b.pausedTh"
           class="bcr-banner">{{ b.pausedTh }}</div>
      <p class="bcr-progtext">{{ $t("broadcast.screens.report.progress", { sent: num(processed), total: num(totals.total) }) }}</p>
      <div class="bcr-prog"
           role="progressbar"
           :aria-label="$t('broadcast.screens.report.progress_label')"
           aria-valuemin="0"
           aria-valuemax="100"
           :aria-valuenow="percent">
        <span :style="{ width: percent + '%' }"></span>
      </div>
      <div class="bcr-counters">
        <span class="bcr-counter">{{ $t("broadcast.screens.home.delivered", { n: num(totals.delivered) }) }}</span>
        <span class="bcr-counter">{{ $t("broadcast.screens.home.read", { n: num(totals.read) }) }}</span>
        <span class="bcr-counter">{{ $t("broadcast.screens.home.failed", { n: num(totals.failed) }) }}</span>
      </div>

      <template v-if="b.failures && b.failures.length">
        <h6 class="bcr-h6">{{ $t("broadcast.screens.report.why_failed") }}</h6>
        <div v-for="f in b.failures"
             :key="f.reason"
             class="bcr-failrow"><span>{{ f.labelTh }}</span><b>{{ num(f.count) }}</b></div>
      </template>

      <h6 class="bcr-h6">{{ $t("broadcast.screens.report.by_page") }}</h6>
      <div class="bcr-search mb-2">
        <i class="fal fa-search"></i>
        <input v-model="query"
               class="form-control"
               :placeholder="$t('broadcast.screens.report.search')"
               autocomplete="off" />
      </div>
      <div class="bcr-pages">
        <div v-for="row in shownPages"
             :key="row.pageId"
             class="bcr-pagerow">
          <PageAvatar :page="avatarOf(row)"
                      size="sm" />
          <span class="bcr-pagename">{{ row.name }}</span>
          <span class="bcr-ppn">{{ num(row.sent) }}/{{ num(row.total) }}</span>
        </div>
        <p v-if="!shownPages.length"
           class="bcr-note">{{ $t("broadcast.screens.report.no_page") }}</p>
      </div>
      <button v-if="!query.trim() && !showAll && pageRows.length > 20"
              type="button"
              class="btn btn-outline-primary w-100 mt-2"
              @click="showAll = true">{{ $t("broadcast.screens.report.show_all") }}</button>

      <div class="bcr-rowbtns mt-3">
        <template v-if="b.status === 'sending'">
          <button type="button"
                  class="btn btn-outline-primary"
                  :disabled="busy"
                  @click="act('pauseBroadcast')">{{ $t("broadcast.screens.report.pause") }}</button>
          <button type="button"
                  class="btn btn-default"
                  :disabled="busy"
                  @click="askCancel">{{ $t("broadcast.screens.report.cancel") }}</button>
        </template>
        <template v-else-if="b.status === 'paused'">
          <button type="button"
                  class="btn btn-primary"
                  :disabled="busy"
                  @click="act('resumeBroadcast')">{{ $t("broadcast.screens.report.resume") }}</button>
          <button type="button"
                  class="btn btn-default"
                  :disabled="busy"
                  @click="askCancel">{{ $t("broadcast.screens.report.cancel") }}</button>
        </template>
      </div>
      <button type="button"
              class="btn btn-default w-100 mt-2"
              @click="$emit('back')">{{ $t("broadcast.screens.report.back") }}</button>
    </div>

    <BcSheet v-if="confirming"
             :title="$t('broadcast.screens.report.cancel_title', { n: num(left) })"
             :locked="busy"
             @close="confirming = false">
      <button type="button"
              class="btn btn-primary btn-lg w-100 mb-2"
              :disabled="busy"
              @click="doCancel">{{ $t("broadcast.screens.report.cancel_yes") }}</button>
      <button type="button"
              class="btn btn-default btn-lg w-100"
              :disabled="busy"
              @click="confirming = false">{{ $t("broadcast.screens.report.cancel_no") }}</button>
    </BcSheet>
  </div>
</template>

<script>
import BcSheet from "@/components/derived/broadcast/BcSheet";
import BcStatusPill from "@/components/derived/broadcast/BcStatusPill";
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { bcToast } from "@/components/derived/broadcast/toast";

const POLL_MS = 5000;

// One broadcast's report: counts, failures by reason, per Page, pause / resume / cancel.
// `id` is the broadcast; `pages` (GET /api/broadcast/pages) only supplies the Page pictures.
// Polled every 5 s while the broadcast is sending.
export default {
  name: "BcReport",
  components: { BcSheet, BcStatusPill, PageAvatar },
  props: {
    id: { type: [String, Number], required: true },
    pages: { type: Array, default: () => [] },
  },

  data() {
    return { b: null, loading: true, error: "", query: "", showAll: false, busy: false, confirming: false, timer: null };
  },

  computed: {
    totals() {
      return Object.assign({ total: 0, queued: 0, held: 0, sent: 0, delivered: 0, read: 0, failed: 0 }, (this.b && this.b.totals) || {});
    },
    processed() {
      return Math.min(this.totals.total, this.totals.sent + this.totals.failed);
    },
    percent() {
      return this.totals.total ? Math.round((this.processed / this.totals.total) * 100) : 0;
    },
    left() {
      return this.totals.queued + this.totals.held;
    },
    autopaused() {
      return this.b.status === "paused" && this.b.pausedReason && this.b.pausedReason !== "seller";
    },
    title() {
      const b = this.b;
      const prefix = this.$t(b.kind === "utility" ? "broadcast.screens.home.prefix_all" : "broadcast.screens.home.prefix_24");
      return prefix + (b.groupsTh || "");
    },
    whenText() {
      const d = new Date(this.b.createdAt);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => ("0" + n).slice(-2);
      return pad(d.getDate()) + "/" + pad(d.getMonth() + 1) + "/" + d.getFullYear() + " " + pad(d.getHours()) + ":" + pad(d.getMinutes());
    },
    pageRows() {
      return ((this.b && this.b.byPage) || []).slice().sort((a, c) => c.total - a.total);
    },
    shownPages() {
      const q = this.query.trim().toLowerCase();
      if (q) return this.pageRows.filter((r) => String(r.name || "").toLowerCase().includes(q));
      return this.showAll ? this.pageRows : this.pageRows.slice(0, 20);
    },
  },

  watch: {
    id() {
      this.b = null;
      this.loading = true;
      this.load();
    },
  },

  created() {
    this.load();
    this.timer = setInterval(() => {
      if (this.b && this.b.status === "sending") this.load(true);
    }, POLL_MS);
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  methods: {
    num(n) {
      return Number(n || 0).toLocaleString("en-US");
    },

    avatarOf(row) {
      const page = this.pages.find((p) => String(p.pageId) === String(row.pageId));
      return { pageId: row.pageId, name: row.name, pictureUrl: (page && page.picture) || null };
    },

    load(quiet) {
      const id = this.id;
      this.$store.dispatch("broadcast/fetchBroadcast", {
        id,
        callback: (ok, data) => {
          if (String(id) !== String(this.id)) return;
          this.loading = false;
          if (ok) {
            this.b = data;
            this.error = "";
          } else if (!quiet) {
            this.error = data.messageTh || "";
          }
        },
      });
    },

    act(action) {
      if (this.busy) return;
      this.busy = true;
      this.$store.dispatch("broadcast/" + action, {
        id: this.id,
        callback: (ok, data) => {
          this.busy = false;
          if (ok) this.load(true);
          else bcToast(data.messageTh, "danger");
        },
      });
    },

    askCancel() {
      this.confirming = true;
    },

    doCancel() {
      if (this.busy) return;
      this.busy = true;
      this.$store.dispatch("broadcast/cancelBroadcast", {
        id: this.id,
        callback: (ok, data) => {
          this.busy = false;
          this.confirming = false;
          if (ok) this.load(true);
          else bcToast(data.messageTh, "danger");
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.bcr-load,
.bcr-error {
  text-align: center;
  padding: 2rem 0;
  color: #666;
}

.bcr-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.bcr-title {
  min-width: 0;
}

.bcr-q {
  font-size: 1.35rem;
  font-weight: 300;
  color: #333;
  margin: 0;
}

.bcr-note {
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.bcr-title .bcr-note {
  margin-top: 0.25rem;
}

.bcr-banner {
  background: #fde3e3;
  border: 1px solid #fdd8d8;
  color: #803c3c;
  font-weight: 700;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin: 0.75rem 0;
}

.bcr-progtext {
  color: #333;
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0.75rem 0 0.4rem;
}

.bcr-prog {
  height: 12px;
  border-radius: 6px;
  background: #e6e6e6;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: $brand-blue-mid;
    border-radius: 6px;
    transition: width 0.8s linear;
  }
}

.bcr-counters {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.bcr-counter {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0.5rem 0.25rem;
  background: #f3f3f3;
  border-radius: 10px;
  color: #333;
  font-weight: 700;
  font-size: 0.9375rem;
  text-align: center;
}

.bcr-h6 {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin: 1.1rem 0 0.5rem;
}

.bcr-failrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 44px;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f3f3f3;
  color: #333;

  &:last-of-type {
    border-bottom: 0;
  }

  b {
    font-size: 1.05rem;
  }
}

.bcr-search {
  position: relative;

  .fal {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
  }

  .form-control {
    padding-left: 2.1rem;
    min-height: 44px;
  }
}

.bcr-pages {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.bcr-pagerow {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 60px;
  padding: 0.6rem 0.75rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
}

.bcr-pagename {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
}

.bcr-ppn {
  flex: 0 0 auto;
  color: #333;
  font-size: 0.9375rem;
  white-space: nowrap;
}

.bcr-rowbtns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .btn {
    flex: 1 1 auto;
  }
}

.btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

body.--dark {
  .bcr-q,
  .bcr-progtext,
  .bcr-counter,
  .bcr-h6,
  .bcr-failrow,
  .bcr-pagename,
  .bcr-ppn {
    color: #e6e6e6;
  }

  .bcr-note,
  .bcr-load,
  .bcr-error {
    color: #c4c4c4;
  }

  .bcr-counter {
    background: #4b4b4b;
  }

  .bcr-prog {
    background: #555;
  }

  .bcr-failrow {
    border-bottom-color: #555;
  }

  .bcr-pagerow {
    background: #4b4b4b;
    border-color: #555;
  }

  .bcr-search .form-control {
    background: #4b4b4b;
    border-color: #555;
    color: #e6e6e6;
  }

  .bcr-banner {
    background: #5a3a3a;
    border-color: #6b4747;
    color: #ffc9c9;
  }
}
</style>

